import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Trash2, Pin } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";

const REACTIONS = [
  { key: "like", emoji: "👍", label: "Like" },
  { key: "dislike", emoji: "👎", label: "Dislike" },
  { key: "laugh", emoji: "😂", label: "Laugh" },
  { key: "rocket", emoji: "🚀", label: "Rocket" },
  { key: "fire", emoji: "🔥", label: "Fire" },
];

export default function CommunityPostCard({ post, onChanged }) {
  const { user } = useAuth();
  const [reactionCounts, setReactionCounts] = useState({});
  const [userReaction, setUserReaction] = useState(null);
  const [showReactions, setShowReactions] = useState(false);
  const reactionRef = React.useRef(null);

  useEffect(() => {
    api.get("/community/posts/" + post.id + "/reactions")
      .then((r) => {
        setReactionCounts(r.data.counts || {});
        setUserReaction(r.data.user_reaction || null);
      })
      .catch(() => {});
  }, [post.id]);

  useEffect(() => {
    const handleClick = (e) => {
      if (reactionRef.current && !reactionRef.current.contains(e.target)) {
        setShowReactions(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const react = async (reactionKey) => {
    if (!user) return toast.error("Sign in to react");
    try {
      const { data } = await api.post("/community/posts/" + post.id + "/react", { reaction: reactionKey });
      setReactionCounts(data.counts || {});
      setUserReaction(data.user_reaction);
      setShowReactions(false);
    } catch (_) {
      toast.error("Could not react");
    }
  };

  const pin = async (e) => {
    e.preventDefault();
    try {
      if (post.pinned) {
        await api.post("/community/posts/" + post.id + "/unpin");
        toast.success("Post unpinned");
      } else {
        await api.post("/community/posts/" + post.id + "/pin");
        toast.success("Post pinned");
      }
      onChanged && onChanged();
    } catch (_) {
      toast.error("Could not pin post");
    }
  };

  const remove = async (e) => {
    e.preventDefault();
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete("/community/posts/" + post.id);
      toast.success("Post deleted");
      onChanged && onChanged();
    } catch (_) {
      toast.error("Could not delete post");
    }
  };

  const totalReactions = Object.values(reactionCounts).reduce((a, b) => a + b, 0);
  const canDelete = user && (user.id === post.author_id || user.role === "admin");
  const isAdmin = user?.role === "admin";

  return (
    <article
      data-testid={"community-post-" + post.id}
      className={"bg-[#0a0a0b] border p-6 hover:border-white/20 transition " + (post.pinned ? "border-yellow-400/40" : "border-white/10")}
    >
      {/* Pinned badge */}
      {post.pinned && (
        <div className="flex items-center gap-1 text-yellow-400 text-xs font-mono uppercase tracking-widest mb-3">
          <Pin className="w-3 h-3" /> Pinned
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-yellow-400 text-black font-bold flex items-center justify-center text-sm flex-shrink-0">
          {(post.author_name || "U")[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white">{post.author_name}</div>
          <div className="text-xs font-mono text-zinc-500">
            {new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isAdmin && (
            <button
              onClick={pin}
              className={"transition " + (post.pinned ? "text-yellow-400" : "text-zinc-500 hover:text-yellow-400")}
              title={post.pinned ? "Unpin post" : "Pin post"}
            >
              <Pin className="w-4 h-4" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={remove}
              data-testid={"community-post-delete-" + post.id}
              className="text-zinc-500 hover:text-red-400 transition"
              title="Delete post"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <Link to={"/community/" + post.id} className="block group">
        <h3 className="font-display font-bold text-xl text-white group-hover:text-yellow-400 transition leading-snug">
          {post.title}
        </h3>
        <p className="mt-3 text-zinc-300 leading-relaxed line-clamp-6 whitespace-pre-wrap break-all">
          {post.content}
        </p>
      </Link>

      <div className="mt-5 pt-4 border-t border-white/5 flex items-center gap-4 text-sm flex-wrap">

        {/* Reaction button */}
        <div ref={reactionRef} className="relative">
          <button
            onClick={() => setShowReactions(!showReactions)}
            className={"flex items-center gap-2 transition px-2 py-1 border rounded-full text-xs " +
              (userReaction ? "border-yellow-400/40 text-yellow-400" : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white")}
          >
            <span>{userReaction ? REACTIONS.find((r) => r.key === userReaction)?.emoji : "😊"}</span>
            <span className="font-mono">{totalReactions > 0 ? totalReactions : "React"}</span>
          </button>

          {/* Reaction picker */}
          {showReactions && (
            <div className="absolute bottom-full left-0 mb-2 bg-zinc-900 border border-white/10 rounded-full px-3 py-2 flex items-center gap-2 z-10 shadow-xl">
              {REACTIONS.map((r) => (
                <button
                  key={r.key}
                  onClick={() => react(r.key)}
                  title={r.label}
                  className={"text-xl hover:scale-125 transition-transform " +
                    (userReaction === r.key ? "opacity-100 scale-125" : "opacity-70 hover:opacity-100")}
                >
                  {r.emoji}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Reaction counts */}
        {Object.entries(reactionCounts).map(([key, count]) => {
          if (!count) return null;
          const reaction = REACTIONS.find((r) => r.key === key);
          if (!reaction) return null;
          return (
            <span key={key} className="text-xs font-mono text-zinc-500 flex items-center gap-1">
              {reaction.emoji} {count}
            </span>
          );
        })}

        {/* Comments */}
        <Link
          to={"/community/" + post.id}
          data-testid={"community-post-comment-link-" + post.id}
          className="flex items-center gap-2 text-zinc-400 hover:text-yellow-400 transition ml-auto"
        >
          <MessageCircle className="w-4 h-4" />
          <span className="font-mono">{post.comments_count || 0}</span>
        </Link>
      </div>
    </article>
  );
}